// Salary formula reader and calculator, shared by Salary Components, Salary
// Templates and the HR salary screens.
//
// A formula is read in full (tokens -> expression tree) and then worked out,
// instead of the old text find-and-replace, which dropped a "+" between two
// products ("A * 0.5 + B * 0.25" became one long number) and ignored
// ROUND's decimals and a bracket's "%".
//
// Supported:
//   numbers 1500, 0.75 · component codes BASIC, HRA (case-insensitive)
//   + - * / · unary minus · brackets · "10%" and "(A + B)%" (= / 100)
//   comparisons  > < >= <= == = != <>   (true = 1, false = 0)
//   AND, OR, NOT (also && || !)
//   MIN(a, b, ...)  MAX(a, b, ...)  ROUND(x)  ROUND(x, decimals)
//   IF(condition, then, else)  (nested IFs too)
//   ABS(x)  FLOOR(x)  CEIL(x) / CEILING(x)
// Unknown codes count as 0 and dividing by 0 gives 0 (as before), so a
// half-typed formula never breaks a screen.

const FUNCTION_ARGS = {
  MIN: [1, Infinity],
  MAX: [1, Infinity],
  ROUND: [1, 2],
  IF: [3, 3],
  ABS: [1, 1],
  FLOOR: [1, 1],
  CEIL: [1, 1],
  CEILING: [1, 1],
};
export const FORMULA_KEYWORDS = ["AND", "OR", "NOT"];
export const FORMULA_FUNCTION_NAMES = Object.keys(FUNCTION_ARGS);

class FormulaError extends Error {}

const tokenize = (text) => {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) { i += 1; continue; }
    const number = text.slice(i).match(/^(\d+(\.\d+)?|\.\d+)/);
    if (number) {
      tokens.push({ type: "num", value: parseFloat(number[0]) });
      i += number[0].length;
      continue;
    }
    const word = text.slice(i).match(/^[A-Za-z_][A-Za-z0-9_]*/);
    if (word) {
      const upper = word[0].toUpperCase();
      tokens.push(FORMULA_KEYWORDS.includes(upper) ? { type: "op", value: upper } : { type: "id", value: upper });
      i += word[0].length;
      continue;
    }
    const two = text.slice(i, i + 2);
    if ([">=", "<=", "==", "!=", "<>", "&&", "||"].includes(two)) {
      const map = { "&&": "AND", "||": "OR", "<>": "!=" };
      tokens.push({ type: "op", value: map[two] || two });
      i += 2;
      continue;
    }
    if ("+-*/%()<>=!,".includes(ch)) {
      const map = { "=": "==", "!": "NOT" };
      tokens.push({ type: ch === "(" || ch === ")" || ch === "," ? ch : "op", value: map[ch] || ch });
      i += 1;
      continue;
    }
    throw new FormulaError(`Unexpected "${ch}"`);
  }
  return tokens;
};

// Recursive-descent parser. Precedence, lowest first:
// OR · AND · NOT · comparison · + - · * / · unary - + · postfix % · values.
const parse = (text) => {
  const tokens = tokenize(String(text ?? ""));
  let pos = 0;
  const peek = () => tokens[pos];
  const isOp = (...values) => peek()?.type === "op" && values.includes(peek().value);
  const expect = (type) => {
    if (peek()?.type !== type) {
      throw new FormulaError(type === ")" ? "Brackets don't match" : `Expected "${type}"`);
    }
    pos += 1;
  };

  const binary = (next, ...ops) => () => {
    let node = next();
    while (isOp(...ops)) {
      const op = tokens[pos++].value;
      node = { type: "bin", op, left: node, right: next() };
    }
    return node;
  };

  const primary = () => {
    const token = peek();
    if (!token) throw new FormulaError("The formula ends too early");
    if (token.type === "num") { pos += 1; return { type: "num", value: token.value }; }
    if (token.type === "(") {
      pos += 1;
      const inner = orExpr();
      expect(")");
      return inner;
    }
    if (token.type === "id") {
      pos += 1;
      if (peek()?.type === "(") {
        const name = token.value;
        if (!FUNCTION_ARGS[name]) throw new FormulaError(`Unknown function: ${name}`);
        pos += 1;
        const args = [];
        if (peek()?.type !== ")") {
          args.push(orExpr());
          while (peek()?.type === ",") { pos += 1; args.push(orExpr()); }
        }
        expect(")");
        const [min, max] = FUNCTION_ARGS[name];
        if (args.length < min || args.length > max) {
          throw new FormulaError(
            name === "IF" ? "IF needs a condition, a value if true and a value if false" : `Wrong number of values in ${name}()`,
          );
        }
        return { type: "call", name, args };
      }
      return { type: "id", name: token.value };
    }
    if (token.type === ")") throw new FormulaError("Brackets don't match");
    throw new FormulaError("An operator is missing a value");
  };
  const postfix = () => {
    let node = primary();
    while (isOp("%")) { pos += 1; node = { type: "pct", value: node }; }
    return node;
  };
  const unary = () => {
    if (isOp("-", "+")) {
      const op = tokens[pos++].value;
      return op === "-" ? { type: "neg", value: unary() } : unary();
    }
    return postfix();
  };
  const product = binary(unary, "*", "/");
  const sum = binary(product, "+", "-");
  const comparison = () => {
    const left = sum();
    if (isOp(">", "<", ">=", "<=", "==", "!=")) {
      const op = tokens[pos++].value;
      return { type: "bin", op, left, right: sum() };
    }
    return left;
  };
  const notExpr = () => {
    if (isOp("NOT")) { pos += 1; return { type: "not", value: notExpr() }; }
    return comparison();
  };
  const andExpr = binary(notExpr, "AND");
  const orExpr = binary(andExpr, "OR");

  if (!tokens.length) throw new FormulaError("Enter a formula");
  const tree = orExpr();
  if (pos < tokens.length) {
    throw new FormulaError(tokens[pos].type === ")" ? "Brackets don't match" : "An operator is missing between two values");
  }
  return tree;
};

const roundTo = (value, digits) => {
  const factor = 10 ** Math.max(0, Math.min(10, Math.trunc(digits) || 0));
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

const evaluate = (node, values) => {
  switch (node.type) {
    case "num":
      return node.value;
    case "id": {
      const value = Number(values[node.name]);
      return Number.isFinite(value) ? value : 0;
    }
    case "pct":
      return evaluate(node.value, values) / 100;
    case "neg":
      return -evaluate(node.value, values);
    case "not":
      return evaluate(node.value, values) ? 0 : 1;
    case "call": {
      const args = node.args;
      if (node.name === "IF") return evaluate(args[0], values) ? evaluate(args[1], values) : evaluate(args[2], values);
      const nums = args.map((arg) => evaluate(arg, values));
      switch (node.name) {
        case "MIN": return Math.min(...nums);
        case "MAX": return Math.max(...nums);
        case "ROUND": return roundTo(nums[0], nums[1] ?? 0);
        case "ABS": return Math.abs(nums[0]);
        case "FLOOR": return Math.floor(nums[0]);
        default: return Math.ceil(nums[0]);
      }
    }
    default: {
      if (node.op === "AND") return evaluate(node.left, values) && evaluate(node.right, values) ? 1 : 0;
      if (node.op === "OR") return evaluate(node.left, values) || evaluate(node.right, values) ? 1 : 0;
      const a = evaluate(node.left, values);
      const b = evaluate(node.right, values);
      switch (node.op) {
        case "+": return a + b;
        case "-": return a - b;
        case "*": return a * b;
        case "/": return b === 0 ? 0 : a / b;
        case ">": return a > b ? 1 : 0;
        case "<": return a < b ? 1 : 0;
        case ">=": return a >= b ? 1 : 0;
        case "<=": return a <= b ? 1 : 0;
        case "==": return a === b ? 1 : 0;
        default: return a !== b ? 1 : 0;
      }
    }
  }
};

const collectCodes = (node, codes) => {
  if (!node) return codes;
  if (node.type === "id") codes.add(node.name);
  ["value", "left", "right"].forEach((key) => collectCodes(node[key], codes));
  (node.args || []).forEach((arg) => collectCodes(arg, codes));
  return codes;
};

// Syntax check without values: { valid, message, codes } where codes are the
// component codes the formula reads (functions and keywords left out).
export const checkFormulaSyntax = (formula) => {
  try {
    const tree = parse(formula);
    return { valid: true, message: "", codes: [...collectCodes(tree, new Set())] };
  } catch (error) {
    if (!(error instanceof FormulaError)) throw error;
    return { valid: false, message: error.message, codes: [] };
  }
};

// Formula variables that stand for the template gross salary and are not
// salary components themselves. This is the single place that defines them:
// the edit popup and the cross-row recalculation both resolve through it, so
// a row can never be calculated one way and then recalculated another way.
export const GROSS_SALARY_VARIABLES = ["GROSS", "EGS"];

export const isGrossSalaryVariable = (code) =>
  GROSS_SALARY_VARIABLES.includes(String(code ?? "").toUpperCase());

// The formula's value for the given code -> amount map, to 2 decimals.
// 0 for an empty or unreadable formula.
export const calculateFormula = (formula, variableValues = {}) => {
  if (formula === undefined || formula === null || String(formula).trim() === "") return 0;
  const values = {};
  Object.entries(variableValues || {}).forEach(([key, value]) => {
    values[String(key).toUpperCase()] = value;
  });
  let result;
  try {
    result = evaluate(parse(formula), values);
  } catch (error) {
    if (!(error instanceof FormulaError)) throw error;
    return 0;
  }
  return Number.isFinite(result) ? Number(result.toFixed(2)) : 0;
};

// ---- Older helpers, kept for existing imports ----

export const splitTopLevel = (text, delimiter = ",") => {
  const parts = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth -= 1;
    else if (ch === delimiter && depth === 0) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts;
};

export const findMatchingParen = (str, openIndex) => {
  let depth = 0;
  for (let i = openIndex; i < str.length; i++) {
    if (str[i] === "(") depth += 1;
    else if (str[i] === ")") {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
};

// True / false for a condition such as "GROSS > 21000". `evaluate` turns a
// side into a number when given (old callers); otherwise the formula reader
// works the whole condition out.
export const evaluateCondition = (condition, evaluateSide) => {
  if (!evaluateSide) return calculateFormula(condition) !== 0;
  const cond = String(condition).trim();
  for (const op of ["<=", ">=", "!=", "==", "<", ">"]) {
    const index = cond.indexOf(op);
    if (index !== -1) {
      const a = Number(evaluateSide(cond.slice(0, index)));
      const b = Number(evaluateSide(cond.slice(index + op.length)));
      if (Number.isNaN(a) || Number.isNaN(b)) return false;
      switch (op) {
        case "==": return a === b;
        case "!=": return a !== b;
        case ">=": return a >= b;
        case "<=": return a <= b;
        case ">": return a > b;
        default: return a < b;
      }
    }
  }
  return !!Number(evaluateSide(cond));
};
